/**
 * Theme Context — mandatory dark mode state
 *
 * Sets the dark data-theme attribute on <html> and exposes the
 * dark CanvasThemeColors for PixiJS drawing functions.
 */

import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react';
import { DARK_CANVAS, type CanvasThemeColors } from '../lib/theme-colors';

type Theme = 'dark';

interface ThemeContextValue {
  theme: Theme;
  canvasColors: CanvasThemeColors;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme: Theme = 'dark';

  useEffect(() => {
    document.documentElement.dataset.theme = 'dark';
    try {
      localStorage.removeItem('sigtdd_theme');
    } catch { /* ignore */ }
  }, []);

  const canvasColors = DARK_CANVAS;

  return (
    <ThemeContext.Provider value={{ theme, canvasColors }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}
